import { useEffect, useMemo } from "react";
import "./onepager.css";

const AMBITION_NAMES = ["Kevin O'Leary", "Garry Kasparov", "Andrej Karpathy", "Andrew Ng", "Nicolai Tangen", "Inga Strümke"];

const PRINCIPLES = [
  { t: "World-class voices", d: "Celebrity business draw + frontier AI leadership." },
  { t: "The room matters", d: "Executives, founders, investors and senior decision-makers." },
  { t: "No weak links", d: "Premium production, hospitality, design and broadcast." },
  { t: "Experience what's next", d: "A small number of curated robotics, agents and AI activations." },
  { t: "Deliberately limited", d: "Not a mass-market expo. Scarcity is part of the product." },
];

const PROOFS = [
  { l: "~700 in the room", s: "Executives, founders, investors" },
  { l: "Broadcast worldwide", s: "Live digital participation" },
  { l: "AI Experience", s: "Curated frontier activations" },
  { l: "Summit Dinner", s: "One table, one evening" },
];

export const MODES = {
  general: {
    tag: "Executive brief",
    ambition: false,
    stage: false,
    partnership: true,
    exclusivity: false,
    privat: false,
    ctaLines: ["Speaker conversations", "Partnerships", "Founding relationships"],
  },
  speaker: {
    tag: "Speaker brief",
    ambition: true,
    stage: true,
    partnership: false,
    exclusivity: false,
    privat: true,
    ctaLines: ["Speaker conversations", "Founding relationships", "Partnerships"],
  },
  partner: {
    tag: "Partner brief",
    ambition: true,
    stage: false,
    partnership: true,
    exclusivity: true,
    privat: true,
    ctaLines: ["Partnerships", "Founding relationships", "Speaker conversations"],
  },
};

const Sep = () => <span className="sep">·</span>;

const Head = ({ idx, lbl }) => (
  <div className="op-head">
    <span className="idx">{idx}</span>
    <span className="lbl">{lbl}</span>
    <span className="rule" />
  </div>
);

/* Presentasjonslag for Executive One-Pager — delt mellom admin-/public-siden
   og den passordbeskyttede delte visningen. chrome = skjerm-verktøy (skjult i print). */
export default function OnePagerDokument({ mode = "general", ev, chrome = null }) {
  const m = MODES[mode] || MODES.general;

  useEffect(() => {
    document.title = "Norwegian AI Summit 2027 — The Next Shift";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    // @page injiseres runtime så den ikke kolliderer med andre siders print-CSS
    const stil = document.createElement("style");
    stil.id = "op-page-stil";
    stil.textContent = "@page { size: A4 portrait; margin: 0; }";
    document.head.appendChild(stil);
    return () => { document.head.removeChild(meta); document.head.removeChild(stil); };
  }, []);

  // Scroll-koreografi — rolige reveals, av ved reduced motion (print tvinger synlig i CSS)
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".op-reveal"));
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      els.forEach((el) => el.classList.add("in"));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -6% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [mode]);

  const dato = useMemo(() => {
    const d = ev?.eventDate;
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      try {
        return new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
      } catch { /* fallthrough */ }
    }
    return "13 April 2027";
  }, [ev]);

  const venue = (ev?.venue || "Grieghallen, Bergen").split(",")[0].trim();

  // Dynamisk seksjonsindeks — 01 why now, 02 proposition, 03 different, deretter modusavhengig
  let n = 0;
  const neste = () => `0${++n}`;
  const idxWhy = neste();
  const idxProp = neste();
  const idxDiff = neste();
  const idxAmbition = m.ambition ? neste() : null;
  const idxStage = m.stage ? neste() : null;
  const idxPartnership = m.partnership ? neste() : null;

  return (
    <div className="op-root" data-testid="onepager-root" data-mode={mode}>
      {/* Skjerm-chrome — skjult i print */}
      <div className="op-frame op-chrome" aria-hidden="true" />
      {chrome}

      {/* Hero */}
      <header className="op-hero">
        <img className="op-hero-bg" src="/images/vision-cover-v3.webp" alt="" />
        <div className="op-hero-shade" />
        <div className="op-topbar">
          <span className="wordmark op-logo" data-testid="onepager-logo">
            <span className="wordmark__bold">Norwegian</span>
            <span className="wordmark__light">AI Summit</span>
          </span>
          <span className="op-tag" data-testid="onepager-tag">{m.tag}</span>
        </div>
        <div className="op-hero-inner">
          <p className="op-kicker op-rise">Norwegian AI Summit · 2027</p>
          <h1 className="op-h1 op-rise" data-testid="onepager-h1">The Next<br />Shift</h1>
          <div className="op-hero-foot op-rise-2">
            <p className="op-meta">{dato}<Sep />{venue}<Sep />Bergen</p>
            <p className="op-hero-sub">
              The annual premium AI summit for leaders, builders and investors shaping what comes next.
            </p>
          </div>
        </div>
      </header>

      <main className="op-main">
        {/* 01 · Why now */}
        <section className="op-sec op-reveal" data-testid="onepager-whynow">
          <Head idx={idxWhy} lbl="Why now" />
          <div className="op-split">
            <p className="op-statement sm">
              AI is moving fast.<br />No one owns the room. <span className="gul">Yet.</span>
            </p>
            <p className="op-lede">
              AI budgets are rising and direction is scarce. Norway still has no defining annual AI summit. This is it.
            </p>
          </div>
        </section>

        {/* 02 · The proposition */}
        <section className="op-sec op-reveal" data-testid="onepager-proposition">
          <Head idx={idxProp} lbl="The proposition" />
          <div className="op-split">
            <h2 className="op-slab">One day.<br />One room.<br />~700 people.</h2>
            <p className="op-lede">
              A deliberately limited gathering built around world-class content, access, networking and an exceptional live experience.
            </p>
          </div>
          <div className="op-proofs">
            {PROOFS.map((p) => (
              <div className="op-proof" key={p.l}>
                <p className="l">{p.l}</p>
                <p className="s">{p.s}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 03 · What makes it different */}
        <section className="op-sec op-reveal" data-testid="onepager-different">
          <Head idx={idxDiff} lbl="What makes it different" />
          <div className="op-prins">
            {PRINCIPLES.map((p, i) => (
              <div className="op-prin" key={p.t}>
                <span className="n">0{i + 1}</span>
                <span className="t">{p.t}</span>
                <span className="d">{p.d}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Interlude · The place — ekte venue, ekte dato */}
        <figure className="op-venue op-reveal" data-testid="onepager-venue">
          <img src="/images/grieghallen.webp" alt="Grieghallen, Bergen" loading="lazy" />
          <div className="op-venue-shade" />
          <figcaption>
            <p className="t">This is happening here.</p>
            <p className="c">Grieghallen · Bergen — Reserved · {dato}</p>
          </figcaption>
        </figure>

        {/* Speaker ambition — kun speaker/partner */}
        {m.ambition && (
          <section className="op-sec op-reveal" data-testid="onepager-ambition">
            <Head idx={idxAmbition} lbl="Speaker ambition" />
            <p className="op-amb-line">
              A global business headline, a frontier-AI anchor and leading Nordic voices.
            </p>
            <p className="op-amb-navn">
              {AMBITION_NAMES.map((navn, i) => (
                <span key={navn}>{i > 0 && <Sep />}{navn}</span>
              ))}
            </p>
            <p className="op-disclaimer">Target universe only · Participation is not implied unless confirmed</p>
          </section>
        )}

        {/* The stage — kun speaker */}
        {m.stage && (
          <section className="op-sec op-reveal" data-testid="onepager-stage">
            <Head idx={idxStage} lbl="The stage" />
            <div className="op-split">
              <p className="op-statement">A room that matters.<br />A reach that travels.</p>
              <p className="op-lede">
                One main stage, built to broadcast standard — in front of the people who decide what happens next.
              </p>
            </div>
            <div className="op-facts">
              <div className="op-fact">
                <p className="k">The room</p>
                <p className="v">~700 executives, founders and investors. No filler seats.</p>
              </div>
              <div className="op-fact">
                <p className="k">The reach</p>
                <p className="v">Every main-stage session carried far beyond the room.</p>
              </div>
              <div className="op-fact">
                <p className="k">The standard</p>
                <p className="v">Production, hospitality and design worth your best material.</p>
              </div>
            </div>
          </section>
        )}

        {/* Partnership — general + partner */}
        {m.partnership && (
          <section className="op-sec op-reveal" data-testid="onepager-partnership">
            <Head idx={idxPartnership} lbl="Partnership opportunity" />
            <div className="op-split">
              <p className="op-statement">Built<br />with a few.</p>
              <p className="op-lede">
                A small number of category-exclusive partners — each with a meaningful role in the room, not simply logo visibility.
              </p>
            </div>
            <div className="op-roller">
              {["Founding", "Strategic", "Broadcast", "Summit Dinner", "AI Experience"].map((r) => (
                <div className="op-rolle" key={r}>{r}</div>
              ))}
            </div>
            {m.exclusivity && (
              <p className="op-excl" data-testid="onepager-exclusivity">Category exclusivity — never more than one of your kind.</p>
            )}
          </section>
        )}
      </main>

      {/* Closing */}
      <footer className="op-close" data-testid="onepager-closing">
        <p className="op-close-h">Be in the room<br />from the beginning<span className="gul">.</span></p>
        <p className="op-close-lines">
          {m.ctaLines.map((l, i) => (
            <span key={l}>{i > 0 && <Sep />}{l}</span>
          ))}
        </p>
        <div>
          <a className="op-mail" href="mailto:martin@norwegianaisummit.no" data-testid="onepager-mail">martin@norwegianaisummit.no</a>
        </div>
        <p className="op-close-meta">
          Norwegian AI Summit<Sep />Bergen<Sep />{dato}
          {m.privat && <span className="privat">Private brief · shared personally</span>}
        </p>
        <div className="op-signoff op-chrome" aria-hidden="true">Norwegian AI Summit</div>
      </footer>
    </div>
  );
}
